/* src/views/SettingsView.js */
import { ref, reactive, onMounted, computed } from 'vue'
import { CreditCard, CheckCircle, AlertCircle, RefreshCw, Sun, Moon } from 'lucide-vue-next'
import { useThemeStore } from '@/stores/theme'
import SettingsService from '@/services/SettingsService'
import Swal from 'sweetalert2'

export default {
  name: 'SettingsView',

  components: {
    CreditCard, CheckCircle, AlertCircle, RefreshCw, Sun, Moon
  },

  setup() {

    const activeTab = ref('gateway')
    const bookingType = ref('free')
    const currentPage = ref(1)
    const depositType = ref('%')
    const depositValue = ref(0)
    const isTestingStripe = ref(false)
    const stripeConnected = ref(false)
    const themeStore = useThemeStore()
    const transactions = ref([])

    const settings = reactive({
      bookingType: 'free',
      cancelHours: 24,
      cancellable: false,
      depositType: '%',
      depositValue: 0,
      low_wallet_threshold: 0,
      min_recharge_amount: 0,
      notify_booking_confirmed: true,
      notify_low_wallet_balance: false,
      notify_rescheduled: true,
      refundable: false,
      reminder_hours_before: 24,
      reschedulable: false,
      rescheduleHours: 24,
      send_appointment_reminder: true,
      walletEnabled: false,
      stripe_enabled: true,
      paypal_enabled: false,
      satispay_enabled: false,
      klarna_enabled: false,
      scalapay_enabled: false,
      nexi_enabled: false,
      mangopay_enabled: false,
    })

    const filters = reactive({
      dateFrom: '',
      dateTo: '',
      status: ''
    })

    const checkStripeStatus = async () => {
    
      try {
    
        const response = await SettingsService.testStripeConnection()
        console.log("Risposta completa:", response)
        const isSuccess = response.data ? response.data.success : response.success
        stripeConnected.value = Boolean(isSuccess)
        console.log("Stato stripeConnected aggiornato a:", stripeConnected.value)
      } 
      catch (error) {
      
        stripeConnected.value = false
      }
    }

    const fetchSettings = async () => {
    
        try {
    
            const { data } = await SettingsService.getSettings()

            settings.walletEnabled = data.enable_wallet_system
            settings.low_wallet_threshold = parseFloat(data.low_wallet_threshold)
            settings.notify_low_wallet_balance = data.notify_low_wallet_balance
            settings.min_recharge_amount = parseFloat(data.min_recharge_amount)
            settings.bookingType = data.booking_type
            settings.depositType = data.deposit_type
            settings.depositValue = parseFloat(data.deposit_value)
            settings.cancellable = data.allow_user_cancellation
            settings.cancelHours = data.cancellation_limit_hours
            settings.refundable = data.refundable
            settings.reschedulable = data.allow_user_rescheduling
            settings.rescheduleHours = data.rescheduling_limit_hours
            settings.send_appointment_reminder = data.send_appointment_reminder
            settings.reminder_hours_before = data.reminder_hours_before
            settings.notify_booking_confirmed = data.notify_booking_confirmed
            settings.notify_rescheduled = data.notify_rescheduled
            settings.stripe_enabled = data.stripe_enabled ?? true
            settings.paypal_enabled = data.paypal_enabled ?? false
            settings.satispay_enabled = data.satispay_enabled ?? false
            settings.klarna_enabled = data.klarna_enabled ?? false
            settings.scalapay_enabled = data.scalapay_enabled ?? false
            settings.nexi_enabled = data.nexi_enabled ?? false
            settings.mangopay_enabled = data.mangopay_enabled ?? false
            console.log("Settings mappati correttamente:", settings)
        } 
        catch (e) {

            console.error("Errore caricamento:", e)
        }
    }


    // Definizione statica dei gateway
    const gateways = [
      {
        key: 'stripe',
        label: 'Stripe',
        enabledKey: 'stripe_enabled',
        methods: ['Carte', 'Apple Pay', 'Google Pay', 'SEPA'],
        note: 'Apple Pay e Google Pay si configurano dalla dashboard Stripe.',
      },
      {
        key: 'paypal',
        label: 'PayPal',
        enabledKey: 'paypal_enabled',
        methods: ['PayPal', 'Carte', 'Pay Later'],
        note: null,
      },
      {
        key: 'satispay',
        label: 'Satispay',
        enabledKey: 'satispay_enabled',
        methods: ['Satispay'],
        note: 'Ideale per pagamenti rapidi da mobile.',
      },
      {
        key: 'klarna',
        label: 'Klarna',
        enabledKey: 'klarna_enabled',
        methods: ['Paga in 3 rate', 'Paga in 30 giorni'],
        note: 'Il cliente paga a rate, tu ricevi subito l\'intero importo.',
      },
      {
        key: 'scalapay',
        label: 'Scalapay',
        enabledKey: 'scalapay_enabled',
        methods: ['3 rate senza interessi'],
        note: 'BNPL italiano. Nessuna commissione per il cliente.',
      },
      {
        key: 'nexi',
        label: 'Nexi (XPay)',
        enabledKey: 'nexi_enabled',
        methods: ['Carte', 'Bancomat', 'POS'],
        note: 'Leader acquiring in Italia.',
      },
      {
        key: 'mangopay',
        label: 'MangoPay',
        enabledKey: 'mangopay_enabled',
        methods: ['Carte', 'Bonifico', 'Wallet'],
        note: 'Ottimo per marketplace e piattaforme multi-vendor.',
      },
    ]
    
    // Funzione dedicata al salvataggio gateway
    const saveGatewaySettings = async () => {
      const payload = {
        stripe_enabled: settings.stripe_enabled,
        paypal_enabled: settings.paypal_enabled,
        satispay_enabled: settings.satispay_enabled,
        klarna_enabled: settings.klarna_enabled,
        scalapay_enabled: settings.scalapay_enabled,
        nexi_enabled: settings.nexi_enabled,
        mangopay_enabled: settings.mangopay_enabled,
      }
    
      try {
        await SettingsService.saveSettings(payload)
        Swal.fire({
          title: 'Salvato!',
          text: 'Configurazione gateway aggiornata.',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false,
        })
      } catch (e) {
        console.error('Errore salvataggio gateway:', e)
        Swal.fire({
          title: 'Errore',
          text: 'Impossibile salvare la configurazione gateway.',
          icon: 'error',
          confirmButtonText: 'Chiudi',
        })
      }
    }

/*    
    const fetchSettings = async () => {
     
      try {
     
        const { data } = await SettingsService.getSettings()
        settings.walletEnabled = data.wallet_enabled ?? settings.walletEnabled
        settings.bookingType = data.booking_type ?? settings.bookingType
        settings.depositType = data.deposit_type ?? settings.depositType
        settings.depositValue = data.deposit_value ?? settings.depositValue
        settings.cancellable = data.cancellable ?? settings.cancellable
        settings.cancelHours = data.cancel_hours ?? settings.cancelHours
        settings.refundable = data.refundable ?? settings.refundable
        settings.reschedulable = data.reschedulable ?? settings.reschedulable
        settings.rescheduleHours = data.reschedule_hours ?? settings.rescheduleHours
        console.log("Settings caricati e mappati:", settings)
      } 
      catch (e) { 

        console.error("Errore caricamento:", e) 
      }
    }
*/

    const saveSettings = async () => {
    
      const payload = {
  
        enable_wallet_system: settings.walletEnabled, // nota il cambio di nome per corrispondere al DB
        low_wallet_threshold: settings.low_wallet_threshold,
        notify_low_wallet_balance: settings.notify_low_wallet_balance,
        min_recharge_amount: settings.min_recharge_amount,
        
        booking_type: settings.bookingType,
        deposit_type: settings.depositType,
        deposit_value: settings.depositValue,
        
        // Politiche di modifica
        allow_user_cancellation: settings.cancellable,
        cancellation_limit_hours: settings.cancelHours,
        refundable: settings.refundable,
        allow_user_rescheduling: settings.reschedulable,
        rescheduling_limit_hours: settings.rescheduleHours,
        
        // Notifiche e Reminder
        send_appointment_reminder: settings.send_appointment_reminder,
        reminder_hours_before: settings.reminder_hours_before,
        notify_booking_confirmed: settings.notify_booking_confirmed,
        notify_rescheduled: settings.notify_rescheduled,
        notify_admin_cancelled: settings.notify_admin_cancelled // Aggiungi se presente nel DB
      }

      try {
        await SettingsService.saveSettings(payload)
        Swal.fire({
          title: 'Salvato!',
          text: "Le configurazioni di sistema sono state aggiornate.",
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        })
      } 
      catch (e) {
        console.error("Errore salvataggio:", e)
        Swal.fire({
          title: 'Errore',
          text: "Impossibile salvare le impostazioni. Controlla la connessione.",
          icon: 'error',
          confirmButtonText: 'Chiudi'
        })
      }
    }

    const filteredTransactions = computed(() => {
    
      return transactions.value.filter(txn => {
    
        const txnDate = new Date(txn.date).getTime()
        const dateFrom = filters.dateFrom ? new Date(filters.dateFrom).getTime() : null
        const dateTo = filters.dateTo ? new Date(filters.dateTo).getTime() : null
        let matches = true
        

        if (dateFrom && txnDate < dateFrom) matches = false
        
        if (dateTo && txnDate > dateTo) matches = false
        
        if (filters.status && txn.status !== filters.status) matches = false
        
        return matches
      })
    })
    
    const totalPages = computed(() => {

      return Math.ceil(filteredTransactions.value.length / 10) || 1
    })

    const toggleTheme = () => {
      themeStore.toggleTheme()
    }

    /*** REAL TO BE DECOMMENTED AND REMOVED MOCKED VERSION***/
    /*
    const fetchTransactions = async () => {
      try {
        const params = new URLSearchParams({
          page: currentPage.value,
          page_size: 10,
          ...filters
        })

        // Assicurati che l'URL sia corretto per il tuo backend
        const response = await fetch(`/api/payments/transactions/?${params.toString()}`)
        const data = await response.json()
        
        transactions.value = data.results
        totalPages.value = Math.ceil(data.count / 10)
      } catch (error) {
        console.error("Errore caricamento transazioni:", error)
      }
    }
    */

    /* MOCKED VERSION, MUST BE REMOVED */
    const fetchTransactions = async () => {
    
      isTestingStripe.value = true

      const mockData = {
        count: 30,
        results: [
          { id: 'txn_001', date: '2026-06-09', amount: 45.50, status: 'success' },
          { id: 'txn_002', date: '2026-06-09', amount: 120.00, status: 'success' },
          { id: 'txn_003', date: '2026-06-08', amount: 15.00, status: 'failed' },
          { id: 'txn_004', date: '2026-06-08', amount: 85.00, status: 'success' },
          { id: 'txn_005', date: '2026-06-07', amount: 200.00, status: 'success' },
          { id: 'txn_006', date: '2026-06-07', amount: 50.00, status: 'success' },
          { id: 'txn_007', date: '2026-06-06', amount: 35.20, status: 'failed' },
          { id: 'txn_008', date: '2026-06-06', amount: 95.00, status: 'success' },
          { id: 'txn_009', date: '2026-06-05', amount: 12.50, status: 'success' },
          { id: 'txn_010', date: '2026-06-05', amount: 300.00, status: 'success' },
          { id: 'txn_011', date: '2026-06-04', amount: 45.50, status: 'success' },
          { id: 'txn_012', date: '2026-06-04', amount: 120.00, status: 'failed' },
          { id: 'txn_013', date: '2026-06-03', amount: 15.00, status: 'success' },
          { id: 'txn_014', date: '2026-06-03', amount: 85.00, status: 'success' },
          { id: 'txn_015', date: '2026-06-02', amount: 200.00, status: 'success' },
          { id: 'txn_016', date: '2026-06-02', amount: 50.00, status: 'success' },
          { id: 'txn_017', date: '2026-06-01', amount: 35.20, status: 'success' },
          { id: 'txn_018', date: '2026-06-01', amount: 95.00, status: 'failed' },
          { id: 'txn_019', date: '2026-05-31', amount: 12.50, status: 'success' },
          { id: 'txn_020', date: '2026-05-31', amount: 300.00, status: 'success' },
          { id: 'txn_021', date: '2026-05-30', amount: 45.50, status: 'success' },
          { id: 'txn_022', date: '2026-05-30', amount: 120.00, status: 'success' },
          { id: 'txn_023', date: '2026-05-29', amount: 15.00, status: 'success' },
          { id: 'txn_024', date: '2026-05-29', amount: 85.00, status: 'success' },
          { id: 'txn_025', date: '2026-05-28', amount: 200.00, status: 'failed' },
          { id: 'txn_026', date: '2026-05-28', amount: 50.00, status: 'success' },
          { id: 'txn_027', date: '2026-05-27', amount: 35.20, status: 'success' },
          { id: 'txn_028', date: '2026-05-27', amount: 95.00, status: 'success' },
          { id: 'txn_029', date: '2026-05-26', amount: 12.50, status: 'success' },
          { id: 'txn_030', date: '2026-05-26', amount: 300.00, status: 'success' }
        ]
      }
      
      setTimeout(() => {
        transactions.value = mockData.results
        console.log("Transazioni caricate:", transactions.value)
        totalPages.value = Math.ceil(mockData.count / 10)
        isTestingStripe.value = false
      }, 500)
    }

    const paginatedTransactions = computed(() => {
    
      const start = (currentPage.value - 1) * 10
      const end = start + 10
      return filteredTransactions.value.slice(start, end)
    })

    const formatCurrency = (value) => {
      return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value)
    }
    
    const changePage = (page) => {
      if (page >= 1 && page <= totalPages.value) {
        currentPage.value = page
        fetchTransactions()
      }
    }
    
    onMounted(() => {
      
      checkStripeStatus()
      fetchSettings()
      fetchTransactions()
    })

    return {
      activeTab,
      bookingType,
      changePage,
      checkStripeStatus,
      currentPage,
      depositType,
      depositValue,
      fetchTransactions,
      filteredTransactions,
      filters,
      formatCurrency,
      isTestingStripe,
      paginatedTransactions,
      saveSettings,
      settings,
      stripeConnected,
      themeStore,
      toggleTheme,
      totalPages,
      transactions,
      gateways,
      saveGatewaySettings,
    }
  }
}




